import * as THREE from 'three';

/**
 * The world-space mark under the current target (2026-10-16).
 *
 * The only sign of a selection used to be the target frame at the top of the
 * screen — which names a skeleton, and says nothing about *which* of the three
 * in front of you it is. A ring on the floor under it answers that where the
 * player is already looking.
 *
 * One mesh, reused: moved under whatever is targeted and hidden otherwise, so
 * selecting costs no allocation. Drawn only when the target is revealed
 * (`render/monster-reveal.ts`) — a ring would otherwise point straight at a
 * skeleton the darkness is still hiding, the exact tell the reveal rule took
 * away from the rim and HP bar. Red for an enemy, green for a squadmate.
 */
const ENEMY_COLOR = 0xe0483a;
const ALLY_COLOR = 0x7fd17a;
/** Just above the floor, so it never z-fights the tiles. */
const LIFT_M = 0.04;

export class TargetRing {
  private readonly mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private readonly reducedMotion: boolean;
  private elapsed = 0;

  constructor(private readonly scene: THREE.Scene) {
    const geometry = new THREE.RingGeometry(0.62, 0.78, 48);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: ENEMY_COLOR, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.reducedMotion = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  /** `at` null hides it. `y` is the body's own root height. */
  update(at: { x: number; y: number; z: number } | null, hostile: boolean, dtSeconds: number): void {
    if (!at) { this.mesh.visible = false; return; }
    this.mesh.visible = true;
    this.mesh.position.set(at.x, at.y + LIFT_M, at.z);
    this.mesh.material.color.setHex(hostile ? ENEMY_COLOR : ALLY_COLOR);
    this.elapsed += dtSeconds;
    // A slow breath, not a blink — it is a marker, not an alarm.
    this.mesh.material.opacity = this.reducedMotion ? 0.85 : 0.7 + 0.2 * Math.sin(this.elapsed * 4);
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
